import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ManageSubscriptionDialog } from "./subscription/ManageSubscriptionDialog";

export const SubscriptionSettings = () => {
  const { data: subscription, isLoading } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Non authentifié");

        const { data, error } = await supabase
          .from("subscriptions")
          .select("*")
          .eq("owner_id", user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error("Erreur lors de la récupération de l'abonnement:", error);
          toast.error("Erreur lors de la récupération de l'abonnement");
          throw error;
        }

        // If no subscription found, return default free plan
        if (!data) {
          console.log("Aucun abonnement trouvé, utilisation du plan gratuit par défaut");
          return {
            plan: "free",
            product_limit: 3
          };
        }

        console.log("Abonnement trouvé:", data);
        return data;
      } catch (error) {
        console.error("Erreur inattendue:", error);
        toast.error("Une erreur est survenue lors de la récupération de l'abonnement");
        throw error;
      }
    },
  });

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case "premium":
        return "bg-[#33C3F0]/10 text-[#33C3F0] border-[#33C3F0]";
      case "standard":
        return "bg-[#F97316]/10 text-[#F97316] border-[#F97316]";
      default:
        return "bg-gray-100 text-gray-600 border-gray-600";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Abonnement</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium">Plan actuel</p>
            <Badge className={`${getPlanBadgeColor(subscription?.plan)}`}>
              {subscription?.plan === "free" ? "Gratuit" : 
               subscription?.plan === "standard" ? "Standard" : "Premium"}
            </Badge>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">Limite de produits</p>
            <p className="text-2xl font-bold">
              {subscription?.product_limit === -1 ? "Illimité" : subscription?.product_limit}
            </p>
          </div>
        </div>

        <div className="pt-4 border-t">
          <ManageSubscriptionDialog currentPlan={subscription?.plan || "free"} />
        </div>
      </CardContent>
    </Card>
  );
};